import type { PlayerErrorCode, PlayerStatus } from "@hybrid-iptv/player";
import { usePlayerStore } from "../player/playerStore.js";
import { useMultiviewStore } from "../player/multiviewStore.js";

/** The three states between pressing a channel and a picture appearing. */
const PENDING_LABEL: Partial<Record<PlayerStatus, string>> = {
  loading: "Loading…",
  buffering: "Buffering…",
  // Quiet on purpose (owner, 2026-10-19): a retry or a move to a backup address is the
  // player still trying, not an error, so it reads like the first attempt does.
  retrying: "Connecting…",
};

/**
 * A plain-language name for the failure, under the player's own sentence. The
 * sentence says what happened; this says what kind of thing it was, which is
 * what a viewer reads first when they are deciding whether to try the channel
 * again.
 *
 * `exhausted` means exactly one thing here: the free-tier meter stopped
 * playback because the allowance is spent (playerStore). It used to also carry
 * a channel that never started — every retry spent — so a dead channel was
 * labelled "Free viewing time is used up" next to a sentence saying it timed
 * out. `timeout` is that case now, and keeping the two codes apart is what
 * keeps the meter's words honest.
 */
const FAILURE_LABEL: Record<PlayerErrorCode, string> = {
  unsupported: "This stream cannot be played here",
  manifest: "The channel did not start",
  network: "The channel could not be reached",
  media: "Playback stopped",
  autoplay: "Playback needs a press to start",
  timeout: "The stream never started",
  unavailable: "The channel could not be reached",
  exhausted: "Free viewing time is used up",
};

/**
 * The preview pane's own status line: a spinner while the stream is coming up,
 * and the reason when it fails.
 *
 * The pane has no toolbar on purpose (owner, 2026-09-22: it is a PICTURE, not a
 * player — the guide owns every action the transport row duplicated). Taking
 * the row away also took away the only place the viewer could see that a
 * channel was still loading, or WHY it never appeared, so a channel that failed
 * to start simply looked like a channel that was not there. This puts the
 * information back and nothing else: no buttons, nothing focusable, no pointer
 * target — `pointer-events: none`, so a remote or a click can never land on it.
 *
 * It renders in BOTH layouts, and each anchors it differently (see epg.css):
 * the traditional pane is a box at the top of the guide, while the overlay
 * layout's picture is the whole screen behind the grid, where the layer has to
 * be raised above the grid to stay readable.
 *
 * Which statuses are worth showing, and why the rest are silent:
 * `loading`, `buffering` and `retrying` are the three the player passes through
 * before a picture exists — `retrying` is the player's own automatic retry, so
 * the viewer sees the attempt rather than a frozen frame. `error` is terminal
 * and carries a sentence written for a viewer (packages/player's
 * `describeFailure` puts the HTTP status or the decode reason in it), which is
 * exactly what "list the error" means here. Everything else — playing, paused,
 * idle, ended — is either visible on the picture already or about a stream that
 * is no longer on screen.
 */
export function PreviewStatus() {
  const source = usePlayerStore((s) => s.source);
  const status = usePlayerStore((s) => s.status);
  const error = usePlayerStore((s) => s.error);
  // Multiview's primary tile also plays through this store as a preview, and
  // its overlay covers the guide: the grid's own tiles carry their state, so
  // this layer stays out of it.
  const multiviewActive = useMultiviewStore((s) => s.active);

  if (!source || multiviewActive) return null;

  // Full screen needs no condition here: the fullscreen surface paints at
  // z-index 9999 in both the browser's own fullscreen and the APK's app-level
  // full screen, so this layer is behind it by geometry, not by a check that
  // could disagree with what the viewer sees.
  if (status === "error") {
    return (
      <div className="epg-preview-status epg-preview-status--error" role="status" aria-live="polite">
        <div className="epg-preview-status__card">
          <span className="epg-preview-status__detail">{FAILURE_LABEL[error?.code ?? "media"]}</span>
          <span className="epg-preview-status__reason">{error?.message ?? "The stream could not be played."}</span>
        </div>
      </div>
    );
  }

  const label = PENDING_LABEL[status];
  if (!label) return null;

  return (
    <div className="epg-preview-status" role="status" aria-live="polite">
      <span className="epg-preview-status__spinner" aria-hidden="true" />
      <span className="epg-preview-status__label">{label}</span>
    </div>
  );
}

